import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobPool, guardGrabJob, jobDisplayNumber } from "../../services/firestore";
import { guardReadiness } from "../../utils/guardReadiness";
import "./GuardLayout.css";

const TYPE_CHIP = {
  inbound:   { label: "Inbound",    cls: "g-chip-blue" },
  outbound:  { label: "Outbound",   cls: "g-chip-teal" },
  roundtrip: { label: "Round trip", cls: "g-chip-purple" },
  direct:    { label: "Direct",     cls: "g-chip-amber" },
};

const STATUS_CHIP = {
  grabbed:    { label: "Waiting for confirmation", cls: "g-chip-amber" },
  confirmed:  { label: "Confirmed — ready to start", cls: "g-chip-green" },
  inProgress: { label: "In progress", cls: "g-chip-teal" },
};

export default function JobPool() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [poolJobs, setPoolJobs] = useState([]);
  const [loadingPool, setLoadingPool] = useState(true);
  const [grabbing, setGrabbing] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsub = watchJobPool(userProfile.companyId, (data) => {
      setPoolJobs(data);
      setLoadingPool(false);
    });
    return unsub;
  }, [userProfile]);

  const readiness = guardReadiness(userProfile);

  async function handleGrab(job) {
    // Hard gate — Orion needs photo + phone + TWIC for clearance audits.
    if (!readiness.ready) {
      alert(
        `Finish your profile before grabbing jobs.\n\nMissing:\n• ${readiness.missing.join("\n• ")}`
      );
      navigate("/guard/profile");
      return;
    }
    setGrabbing(job.id);
    const guardNumber = 1; // Default — owner can reassign slot in job detail
    await guardGrabJob(job.id, userProfile.id, guardNumber, userProfile.name, job.guardAssignments, job.guardIds);
    setGrabbing(null);
  }

  function formatDate(ts) {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  return (
    <div style={{ paddingBottom: 12 }}>
      {/* Profile-completeness banner — visible until they've uploaded photo,
          phone, TWIC card, and TWIC expiration. Pool grab is gated on this
          but the banner makes the cause obvious without an alert.
          For brand-new guards (everything missing), use a welcoming first-run
          variant instead of the warning style. */}
      {!readiness.ready && (() => {
        const isBrandNew = readiness.missing.length >= 4; // photo + phone + twic image + twic expiry
        if (isBrandNew) {
          return (
            <div style={{
              margin: "12px 12px", padding: "16px 18px",
              background: "linear-gradient(135deg, #185FA5, #2d7a2d)",
              borderRadius: 10, color: "#fff",
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
                👋 Welcome to VeloceFlow
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
                Before you can grab your first job, we need a few things on file
                so brokers know you're cleared for high-value pickups.
              </div>
              <ul style={{ margin: "0 0 14px 20px", padding: 0, fontSize: 12, lineHeight: 1.6 }}>
                <li>Profile photo (so reps recognize you at pickup)</li>
                <li>Your phone number</li>
                <li>TWIC card photo + expiration date</li>
              </ul>
              <button
                onClick={() => navigate("/guard/profile")}
                style={{
                  background: "#fff", color: "#185FA5",
                  border: "none", borderRadius: 7,
                  padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                Set up my profile →
              </button>
            </div>
          );
        }
        return (
          <div style={{
            margin: "12px 12px", padding: "12px 14px",
            background: "#fff7e0", border: "1px solid #f0c878", borderRadius: 8,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#7a5400", marginBottom: 4 }}>
              ⚠️ Finish your profile to grab jobs
            </div>
            <div style={{ fontSize: 11, color: "#7a5400", marginBottom: 10 }}>
              Almost there — just {readiness.missing.length} item{readiness.missing.length === 1 ? "" : "s"} left.
            </div>
            <ul style={{ margin: "0 0 10px 18px", padding: 0, fontSize: 12, color: "#7a5400" }}>
              {readiness.missing.map((m) => <li key={m}>{m}</li>)}
            </ul>
            <button
              className="g-btn g-btn-primary"
              onClick={() => navigate("/guard/profile")}
            >
              Go to profile →
            </button>
          </div>
        );
      })()}

      {/* Pool */}
      <div className="g-section-label">Available jobs</div>
      {loadingPool ? (
        <div style={{ padding: "20px 16px", fontSize: 12, color: "#aaa", textAlign: "center" }}>Loading pool...</div>
      ) : poolJobs.length === 0 ? (
        <div className="g-empty">
          <div className="g-empty-icon">🔍</div>
          <div className="g-empty-text">No jobs in the pool</div>
          <div className="g-empty-sub">Check back soon — new jobs will appear here</div>
        </div>
      ) : poolJobs.slice().sort((a, b) => {
        // Soonest scheduled first; unscheduled at the bottom.
        const da = a.scheduledDate || "9999-99-99";
        const db = b.scheduledDate || "9999-99-99";
        if (da !== db) return da.localeCompare(db);
        return (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99");
      }).map((job) => {
        const tc = TYPE_CHIP[job.jobType] || { label: job.jobType, cls: "g-chip-gray" };
        const pkgCount = job.packages?.length || 0;
        const poolDateStr = (() => {
          if (!job.scheduledDate) return "";
          const [y, m, d] = job.scheduledDate.split("-").map(Number);
          if (!y || !m || !d) return job.scheduledDate;
          return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
        })();
        return (
          <div key={job.id} className="g-card" style={{ margin: "8px 12px" }}>
            <div style={{ padding: "14px 14px 10px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#0B1C2E", marginBottom: 4 }}>
                    {jobDisplayNumber(job) || "—"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span className={`g-chip ${tc.cls}`}>{tc.label}</span>
                    {(job.stops || []).some((s) => s.outOfTown) && (
                      <span style={{
                        background: "#fff7e0", color: "#a36b1d",
                        border: "1px solid #f0c878", borderRadius: 10,
                        padding: "1px 8px", fontSize: 10, fontWeight: 700,
                      }}>OOT</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", fontSize: 11, color: "#888" }}>
                  {poolDateStr ? (
                    <div style={{ fontWeight: 700, color: "#111", fontSize: 12 }}>📅 {poolDateStr}</div>
                  ) : (
                    <div>Created {formatDate(job.createdAt)}</div>
                  )}
                  {/* Airport code stays here on the full card — guards may pick
                      jobs from multiple hubs eventually. OOT shown if applicable. */}
                  {job.jobType !== "direct" && job.airportCode && (
                    <div style={{ fontWeight: 700, color: "#185FA5", fontSize: 13, marginTop: 2 }}>✈ {job.airportCode}</div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#333", fontWeight: 500, marginBottom: 4 }}>{job.brokerName || "—"}</div>
              {job.dispatcherName && <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>{job.dispatcherName}</div>}
              {job.salesRepName && (
                <div style={{ fontSize: 11, color: "#185FA5", fontWeight: 600, marginBottom: 6 }}>
                  👤 {job.salesRepName}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, fontSize: 11, color: "#888", marginBottom: job.stops?.length > 0 ? 8 : 4 }}>
                {pkgCount > 0 && <span>📦 {pkgCount} pkg{pkgCount !== 1 ? "s" : ""}</span>}
                {job.scheduledTime && <span>🕐 {job.scheduledTime}</span>}
              </div>

              {/* Stop route */}
              {job.stops?.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  {job.stops.map((stop, i) => {
                    const icon = stop.type === "airport" ? "✈️" : stop.type === "pickup" ? "📦" : "🏁";
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        {/* Connector line */}
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 16, flexShrink: 0 }}>
                          <div style={{ fontSize: 11, lineHeight: 1 }}>{icon}</div>
                          {i < job.stops.length - 1 && (
                            <div style={{ width: 1, height: 8, background: "#ddd", marginTop: 2 }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 12, color: "#222", fontWeight: 500 }}>
                            {stop.locationName || stop.address || "—"}
                          </span>
                          {stop.scheduledTime && (
                            <span style={{ fontSize: 10, color: "#aaa", marginLeft: 6 }}>{stop.scheduledTime}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {job.notes && (
                <div style={{ fontSize: 11, color: "#666", background: "#f8f9fb", borderRadius: 7, padding: "6px 10px", marginTop: 6 }}>
                  {job.notes}
                </div>
              )}
            </div>
            <div style={{ padding: "0 14px 14px" }}>
              <button
                className={`g-btn ${readiness.ready ? "g-btn-primary" : "g-btn-ghost"}`}
                onClick={() => handleGrab(job)}
                disabled={grabbing === job.id}
                title={!readiness.ready ? `Finish profile first — missing: ${readiness.missing.join(", ")}` : ""}
              >
                {grabbing === job.id
                  ? "Grabbing..."
                  : readiness.ready
                    ? "Grab job"
                    : "🔒 Finish profile to grab"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
